import { Inline } from './Inline'
import { LivePanel } from './LivePanels'
import { ModeCallout } from './ModeCallout'
import type { DocBlock } from './types'

function BulletList({ items, ordered }: { items: string[]; ordered?: boolean }) {
  const List = ordered ? 'ol' : 'ul'
  return (
    <List
      className={
        ordered
          ? 'ml-5 flex list-decimal flex-col gap-1.5 marker:font-semibold marker:text-teal-500'
          : 'ml-5 flex list-disc flex-col gap-1.5 marker:text-teal-300'
      }
    >
      {items.map((item) => (
        <li key={item} className="pl-1">
          <Inline text={item} />
        </li>
      ))}
    </List>
  )
}

interface TableProps {
  caption?: string
  head: string[]
  rows: string[][]
}

/** Below 640 px each row becomes a small stacked block instead of a squeezed table. */
function StackedRows({ caption, head, rows }: TableProps) {
  const labelled = head.length > 2
  return (
    <div className="sm:hidden print:hidden">
      {caption ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-sage">
          {caption}
        </p>
      ) : null}
      <ul className="divide-y divide-line-soft border-y border-line-soft text-sm">
        {rows.map((row) => (
          <li key={row.join('|')} className="py-3">
            <p className="font-medium text-ink break-words">
              <Inline text={row[0] ?? ''} />
            </p>
            <dl className="mt-1 flex flex-col gap-1.5 text-ink-soft">
              {row.slice(1).map((cell, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                <div key={index} className="break-words">
                  <dt className={labelled ? 'text-xs font-semibold text-sage' : 'sr-only'}>
                    {head[index + 1]}
                  </dt>
                  <dd>
                    <Inline text={cell} />
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Table({ caption, head, rows }: TableProps) {
  return (
    <>
      <StackedRows caption={caption} head={head} rows={rows} />
      <div className="docs-table hidden overflow-x-auto sm:block print:block">
        <table className="w-full border-collapse text-left text-sm">
          {caption ? (
            <caption className="mb-2 text-left text-xs font-semibold uppercase tracking-[0.08em] text-sage">
              {caption}
            </caption>
          ) : null}
          <thead>
            <tr className="border-b border-line-strong">
              {head.map((cell) => (
                <th key={cell} scope="col" className="px-3 py-2 font-semibold text-ink first:pl-0">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.join('|')} className="border-b border-line-soft align-top">
                {row.map((cell, index) =>
                  index === 0 ? (
                    <th
                      key={`h-${cell}`}
                      scope="row"
                      className="py-2 pr-3 font-medium text-ink break-words"
                    >
                      <Inline text={cell} />
                    </th>
                  ) : (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                      key={index}
                      className="px-3 py-2 text-ink-soft break-words"
                    >
                      <Inline text={cell} />
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export function DocBlockView({ block }: { block: DocBlock }) {
  switch (block.type) {
    case 'p':
      return (
        <p>
          <Inline text={block.text} />
        </p>
      )
    case 'h':
      return (
        <h3 className="mt-3 font-display text-[17px] font-semibold tracking-[-0.01em] text-ink">
          {block.text}
        </h3>
      )
    case 'list':
      return <BulletList items={block.items} ordered={block.ordered} />
    case 'kv':
      return (
        <Table
          caption={block.caption}
          head={block.head ?? ['Setting', 'What it does']}
          rows={block.rows}
        />
      )
    case 'table':
      return <Table caption={block.caption} head={block.head} rows={block.rows} />
    case 'callout':
      return (
        <ModeCallout mode={block.mode} title={block.title}>
          {block.text ? (
            <p>
              <Inline text={block.text} />
            </p>
          ) : null}
          {block.items ? (
            <ul>
              {block.items.map((item) => (
                <li key={item}>
                  <Inline text={item} />
                </li>
              ))}
            </ul>
          ) : null}
        </ModeCallout>
      )
    case 'live':
      return <LivePanel view={block.view} />
  }
}
